/* eslint-disable @typescript-eslint/no-empty-function */
import { useIsWide } from 'libs/utils/logic/ui/responsiveLogic';
import { createContext, useContext, useEffect, useState } from 'react';
import { CtxModal } from 'session/ctxModal';
import { CtxView } from 'session/ctxView';

export type MapV2MobileModalSize = 'small' | 'large';

export interface IMapV2Context {
    pcModalState: {
        isExpanded: boolean;
        toggle: () => void;
        open: () => void;
        close: () => void;
    };
    features: {
        isHeaderEnabled: boolean;
        isListEnabled: boolean;
        isSearchEnabled: boolean;
        isLanguageSwitchEnabled: boolean;
    };
    mobileModalState: {
        size: MapV2MobileModalSize;
        setSize: (size: MapV2MobileModalSize) => void;
    };
    search: {
        value: string;
        setValue: (value: string) => void;
    };
    fullscreen: {
        isFullscreen: boolean;
        turnOn: () => void;
        toggle: () => void;
    };
    lightGallery: {
        setOpen: (val: boolean) => void;
    };
    routes: {
        getRouteOrderNumberIfExists: (markerId: string) => number | null;
    };
    isWide: boolean;
    isIframe: boolean;
}

interface Props {
    children: React.ReactNode;
}

export const CtxMapV2 = createContext<IMapV2Context>(null);

export const CtxMapV2Provider = ({ children }: Props) => {
    const isWide = useIsWide();
    const ctxModal = useContext(CtxModal);
    const ctxView = useContext(CtxView);
    const [isModalExpanded, setIsModalExpanded] = useState(!isWide);
    const [searchValue, setSearchValue] = useState('');
    const [mobileModalSize, setMobileModalSize] = useState<MapV2MobileModalSize>('small');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isSomeGalleryOpen, setIsSomeGalleryOpen] = useState(false);
    const isIframe = window.parent !== window;

    useEffect(() => {
        if (searchValue) {
            setIsModalExpanded(true);
            ctxModal.setDetailListModal(null);
        }
    }, [searchValue]);

    useEffect(() => {
        if (ctxModal.detailModal?.detail) {
            setIsModalExpanded(true);
        }
    }, [ctxModal.detailModal?.detail]);

    useEffect(() => {
        if (!window.parent) {
            return;
        }

        const data = {
            type: 'fullscreen_changed',
            value: isFullscreen
        };
        window.parent.postMessage(data, '*');
    }, [isFullscreen, ctxView.mapRef?.current]);

    useEffect(() => {
        ctxView.mapRef?.current?.resize();
    }, [ctxView.mapRef?.current, isFullscreen]);

    useEffect(() => {
        function resizeHandler() {
            ctxView.mapRef?.current?.resize();
        }
        window.addEventListener('resize', resizeHandler);
        return () => window.removeEventListener('resize', resizeHandler);
    }, []);

    useEffect(() => {
        function keyHandler(e: KeyboardEvent) {
            // Handles by the gallery
            if (isSomeGalleryOpen) {
                return;
            }
            const isEscape = e.key === 'Escape' || e.key === 'Esc';
            if (isEscape) {
                const isDetailOpen = !!ctxModal.detailModal?.detail;
                const isModalOpenInMobile = !isWide && mobileModalSize === 'large';
                if (isModalOpenInMobile) {
                    setMobileModalSize('small');
                }

                if (isDetailOpen) {
                    ctxModal.setDetailModal(null);
                    return;
                } else if (isFullscreen) {
                    setIsFullscreen(false);
                    return;
                }
            }
        }
        window.addEventListener('keydown', keyHandler);
        return () => window.removeEventListener('keydown', keyHandler);
    }, []);

    const getRouteSearchKey = (mapId: string, markerId: string) => `${mapId}:${markerId}`;
    const routeSearchMap = ctxView.allMarkers.reduce((acc, marker) => {
        if (marker.data.__typename === 'WorldMapMarkerDataVariantIconV2') {
            const num = Number(marker.data.circleText);
            acc.set(
                getRouteSearchKey(ctxView.map.worldMapId, marker.worldMapMarkerId),
                isNaN(num) ? null : num
            );
        }
        return acc;
    }, new Map<string, number>());

    function getRouteOrderNumberIfExists(markerId: string) {
        const index = routeSearchMap.get(getRouteSearchKey(ctxView.map.worldMapId, markerId));
        if (index != null) {
            return index + 1;
        }
        return null;
    }

    const isHeaderEnabled =
        ctxView.map.showList || ctxView.map.guide.availableLanguages?.length > 1;

    useEffect(() => {
        if (ctxModal.detailModal?.detail && !isWide && !ctxView.map.showList) {
            setMobileModalSize('large');
        }
    }, [ctxModal.detailModal?.detail]);

    return (
        <CtxMapV2.Provider
            value={{
                pcModalState: {
                    isExpanded: isModalExpanded,
                    toggle: () => setIsModalExpanded((curr) => !curr),
                    close: () => setIsModalExpanded(false),
                    open: () => setIsModalExpanded(true)
                },
                features: {
                    isListEnabled: ctxView.map.showList,
                    isSearchEnabled: ctxView.map.showSearch,
                    isLanguageSwitchEnabled: ctxView.map.guide.availableLanguages?.length > 1,
                    isHeaderEnabled
                },
                search: {
                    value: searchValue,
                    setValue: setSearchValue
                },
                mobileModalState: {
                    size: mobileModalSize,
                    setSize: setMobileModalSize
                },
                fullscreen: {
                    isFullscreen,
                    toggle: () => setIsFullscreen((curr) => !curr),
                    turnOn: () => setIsFullscreen(true)
                },
                lightGallery: {
                    setOpen: (val) => setIsSomeGalleryOpen(val)
                },
                routes: {
                    getRouteOrderNumberIfExists
                },
                isWide,
                isIframe
            }}
        >
            {children}
        </CtxMapV2.Provider>
    );
};
